import { useState, useEffect } from "react";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import DataTable from "examples/Tables/DataTable";
import { IconButton, Card } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ModeratorModal from "./components/ModeratorModal";
import { useNavigate } from "react-router-dom";
import {
  getModerators,
  changeModeratorStatus,
  deleteModerator,
  resendLoginLink,
} from "services/moderatorService";
import { MODERATOR_STATUS } from "./consts";
import CircularProgress from "@mui/material/CircularProgress";
import { useDebouncedSearch } from "../../../hooks/useDebouncedSearch";
import { toast } from "react-toastify";
import "./ManageModerator.css";
import SearchBar from "../Components/SearchBar";
import AvatarWithLoader from "../Components/Avatar/AvatarWithLoader";

function ManageModerators() {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [editModerator, setEditModerator] = useState(null);
  const [menu, setMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    moderator: null,
  });
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { searchTerm, debouncedSearchTerm, handleSearchChange } =
    useDebouncedSearch(500);

  useEffect(() => {
    setLoading(true);
    getModerators(debouncedSearchTerm || null)
      .then((res) => {
        setModerators(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [refresh, debouncedSearchTerm]);

  const handleOpenModal = (moderator = null) => {
    setEditModerator(moderator);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setEditModerator(null);
    setOpenModal(false);
  };

  const handleMenuClick = (event, moderator) => {
    event.preventDefault();
    const menuWidth = 150;
    setMenu({
      visible: true,
      x: event.clientX - menuWidth,
      y: event.clientY,
      moderator,
    });
  };

  const handleMenuClose = () => {
    setMenu({ ...menu, visible: false, moderator: null });
  };

  const handleDelete = (id) => {
    deleteModerator(id)
      .then(() => {
        setRefresh(!refresh);
        toast.info("Moderator deleted successfully");
      })
      .catch((err) => {
        toast.error(err.response.data.message);
      });
  };

  const handleResendLoginLink = (id) => {
    resendLoginLink(id)
      .then(() => {
        setRefresh(!refresh);
        toast.info("Login link sent to moderator");
      })
      .catch((err) => {
        toast.error(err.response.data.message);
      });
  };

  const handleChangeModeratorStatus = (id, status) => {
    changeModeratorStatus(id, status)
      .then(() => {
        setModerators((prev) =>
          prev.map((m) => (m._id === id ? { ...m, status: status } : m))
        );
        toast.info(
          status === MODERATOR_STATUS.ACTIVE
            ? "Moderator activated successfully."
            : "Moderator suspended successfully. They will not be able to login until they are unsuspended."
        );
      })
      .catch((err) => {
        toast.error(err.response.data.message);
      });
  };

  const columns = [
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ row }) => (
        <ArgonBox display="flex" alignItems="center">
          <AvatarWithLoader src={row.original.avatar} alt={row.original.name} />
          <ArgonTypography fontWeight="medium" variant="h5">
            {row.original.name}
          </ArgonTypography>
        </ArgonBox>
      ),
    },
    {
      Header: "Email",
      accessor: "email",
      Cell: ({ row }) => (
        <ArgonTypography variant="h5" fontWeight="medium">
          {row.original.email}
        </ArgonTypography>
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <span
          style={{
            background:
              value === MODERATOR_STATUS.ACTIVE ? "#209653" : "#f2c94c",
            color: "#fff",
            borderRadius: 12,
            padding: "4px 16px",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {value === MODERATOR_STATUS.ACTIVE ? "Active" : "Suspend"}
        </span>
      ),
    },
    {
      Header: "Action",
      accessor: "action",
      Cell: ({ row }) => {
        return (
          <IconButton onClick={(e) => handleMenuClick(e, row.original)}>
            <MoreVertIcon />
          </IconButton>
        );
      },
    },
  ];

  return (
    <>
      <SearchBar
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Search moderators"
      />
      <ArgonBox display="flex" justifyContent="flex-end" py={3}>
        <ArgonButton
          variant="outlined"
          color="#ffffff"
          onClick={() => handleOpenModal()}
        >
          Add New Moderator
        </ArgonButton>
      </ArgonBox>
      <ArgonBox>
        <Card sx={{ overflow: "visible", padding: 3 }}>
          <ArgonBox p={3}>
            <ArgonTypography variant="h5" fontWeight="medium">
              Moderator Management
            </ArgonTypography>
          </ArgonBox>
          {loading ? (
            <ArgonBox display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </ArgonBox>
          ) : moderators.length > 0 ? (
            <DataTable
              table={{ columns, rows: moderators }}
              entriesPerPage={false}
              canSearch={false}
              showTotalEntries={false}
              pagination={false}
            />
          ) : (
            <ArgonBox display="flex" justifyContent="center" p={3}>
              <ArgonTypography variant="h6">
                No moderators found
              </ArgonTypography>
            </ArgonBox>
          )}
          {menu.visible && (
            <div
              className="manage-moderator:context-menu"
              style={{ top: menu.y, left: menu.x, position: "fixed" }}
              onMouseLeave={handleMenuClose}
            >
              <div
                className="manage-moderator:menu-item"
                onClick={() => {
                  navigate(`/admin/manage-moderator/${menu.moderator._id}`, {
                    state: {
                      moderator: {
                        name: menu.moderator.name,
                        avatar: menu.moderator.avatar,
                      },
                    },
                  });
                  handleMenuClose();
                }}
              >
                Audit Trail
              </div>
              <div
                className="manage-moderator:menu-item"
                onClick={() => {
                  handleChangeModeratorStatus(
                    menu.moderator._id,
                    menu.moderator.status === MODERATOR_STATUS.ACTIVE
                      ? MODERATOR_STATUS.SUSPEND
                      : MODERATOR_STATUS.ACTIVE
                  );
                  handleMenuClose();
                }}
              >
                {menu.moderator.status === MODERATOR_STATUS.ACTIVE
                  ? "Suspend Moderator"
                  : "Activate Moderator"}
              </div>
              <div
                className="manage-moderator:menu-item"
                onClick={() => {
                  handleResendLoginLink(menu.moderator._id);
                  handleMenuClose();
                }}
              >
                Resend login link
              </div>
              <div
                className="manage-moderator:menu-item manage-moderator:delete"
                onClick={() => {
                  handleDelete(menu.moderator._id);
                  handleMenuClose();
                }}
              >
                Delete Moderator
              </div>
            </div>
          )}
          {openModal && (
            <ModeratorModal
              open={openModal}
              handleClose={handleCloseModal}
              moderator={editModerator}
              onUpdate={() => {
                setRefresh(!refresh);
              }}
            />
          )}
        </Card>
      </ArgonBox>
    </>
  );
}

export default ManageModerators;
